import { currentEntryHref, isRecord } from '@/src/lib/navigation/historyEntryState';
import type { MapView } from './mapViewTypes';

const SEARCH_MAP_HISTORY_STATE_KEY = '__hotpadsSearchMapView';
const SEARCH_MAP_HISTORY_STATE_VERSION = 1;
const MAX_MERCATOR_LAT = 85.05112878;
const MAX_ZOOM = 21;

type SearchMapHistoryView = MapView & {
  version: typeof SEARCH_MAP_HISTORY_STATE_VERSION;
  entryHref: string;
};

export function isValidMapView(value: unknown): value is MapView {
  if (!isRecord(value) || !isRecord(value.center)) return false;

  const { lat, lng } = value.center;
  const { zoom } = value;
  return (
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -MAX_MERCATOR_LAT &&
    lat <= MAX_MERCATOR_LAT &&
    typeof lng === 'number' &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180 &&
    typeof zoom === 'number' &&
    Number.isFinite(zoom) &&
    zoom >= 0 &&
    zoom <= MAX_ZOOM
  );
}

function readSearchMapHistoryView(state: unknown, entryHref: string): SearchMapHistoryView | null {
  if (!isRecord(state)) return null;

  const snapshot = state[SEARCH_MAP_HISTORY_STATE_KEY];
  if (!isRecord(snapshot)) return null;
  if (snapshot.version !== SEARCH_MAP_HISTORY_STATE_VERSION || snapshot.entryHref !== entryHref) return null;
  if (!isValidMapView(snapshot)) return null;

  return snapshot as SearchMapHistoryView;
}

function hasMapPositionParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('lat') || params.has('lon') || params.has('z');
}

function writeCurrentEntryView(view: MapView): void {
  const entryHref = currentEntryHref();
  const currentState = isRecord(window.history.state) ? window.history.state : {};
  window.history.replaceState(
    {
      ...currentState,
      [SEARCH_MAP_HISTORY_STATE_KEY]: {
        version: SEARCH_MAP_HISTORY_STATE_VERSION,
        entryHref,
        center: view.center,
        zoom: view.zoom,
      } satisfies SearchMapHistoryView,
    },
    '',
    entryHref,
  );
}

export function seedCurrentSearchMapHistoryView(view: MapView): boolean {
  if (typeof window === 'undefined' || hasMapPositionParams() || !isValidMapView(view)) return false;

  const entryHref = currentEntryHref();
  if (readSearchMapHistoryView(window.history.state, entryHref)) return false;

  writeCurrentEntryView(view);
  return true;
}

export function refineCurrentSearchMapHistoryView(view: MapView): boolean {
  if (typeof window === 'undefined' || hasMapPositionParams() || !isValidMapView(view)) return false;

  const entryHref = currentEntryHref();
  if (!readSearchMapHistoryView(window.history.state, entryHref)) return false;

  writeCurrentEntryView(view);
  return true;
}

export function readCurrentSearchMapHistoryView(state: unknown): MapView | null {
  if (typeof window === 'undefined') return null;

  const snapshot = readSearchMapHistoryView(state, currentEntryHref());
  return snapshot ? { center: snapshot.center, zoom: snapshot.zoom } : null;
}
